<template>
  <div class="cm-list" :class="{ compact: compact }">
    <!-- ── 工具条 ─────────────────────────────── -->
    <div class="card tools-card">
      <div class="searchrow">
        <input
          class="search"
          v-model="keyword"
          placeholder="搜索中文 / 英文 / 拼音，如：大笑、da、laugh"
        />
        <span class="count">共 {{ filtered.length }} / {{ movements.length }}</span>
      </div>
      <div v-if="letters.length" class="letterbar">
        <button :class="{ on: letter === '' }" @click="letter = ''">全部</button>
        <button
          v-for="l in letters"
          :key="l"
          :class="{ on: letter === l }"
          @click="letter = l"
        >{{ l }}</button>
      </div>
    </div>

    <!-- ── 列表 ───────────────────────────────── -->
    <div class="card list-card">
      <div v-if="loading" class="empty">正在载入动作列表…</div>
      <div v-else-if="loadError" class="empty">
        {{ loadError }}
        <br />
        <button class="retry" @click="load">重试</button>
      </div>
      <div v-else-if="!filtered.length" class="empty">没有匹配的动作，换个词试试～</div>
      <div v-else class="list">
        <template v-for="m in filtered">
          <!-- 折叠态 -->
          <div
            v-if="openId !== m.id"
            :key="'c' + m.id"
            class="item"
            @click="openId = m.id"
          >
            <div class="row1">
              <span class="cn">{{ aliasOf(m).cn || '（缺中文名）' }}</span>
              <span class="en">{{ aliasOf(m).en }}</span>
            </div>
            <div class="py">{{ aliasOf(m).py }}</div>
          </div>

          <!-- 展开态 -->
          <div v-else :key="'o' + m.id" class="item on">
            <div class="detail">
              <div class="dtitle">
                <span class="cn">{{ aliasOf(m).cn || '（缺中文名）' }}</span>
                <span class="en">{{ aliasOf(m).en }} · {{ aliasOf(m).py }}</span>
                <button class="close" @click.stop="openId = null">收起 ▲</button>
              </div>

              <!-- 不指定对象 -->
              <div class="block">
                <div class="bhead">
                  <span class="lab">不指定对象</span>
                  <span
                    v-for="x in aliasOf(m).all"
                    :key="'n' + x"
                    class="chip"
                    :title="'复制 ~' + x"
                    @click.stop="copy('~' + x)"
                  >~{{ x }}<span class="cp">复制</span></span>
                </div>
                <div class="views">
                  <div class="view self">
                    <span class="who">你自己看到</span>
                    <span class="say" v-html="paint(m.sourceWithoutTarget, me, target, SRC)"></span>
                  </div>
                  <div class="view">
                    <span class="who">其他人看到</span>
                    <span class="say" v-html="paint(m.othersWithoutTarget, me, target, null)"></span>
                  </div>
                </div>
              </div>

              <!-- 指定对象 -->
              <div class="block">
                <div class="bhead">
                  <span class="lab">指定对象</span>
                  <span
                    v-for="x in aliasOf(m).all"
                    :key="'t' + x"
                    class="chip"
                    :title="'复制 ~' + x + ' ' + target"
                    @click.stop="copy('~' + x + ' ' + target)"
                  >~{{ x }} {{ target }}<span class="cp">复制</span></span>
                </div>
                <div class="views">
                  <div class="view self">
                    <span class="who">你自己看到</span>
                    <span class="say" v-html="paint(m.sourceWithTarget, me, targetShow, SRC)"></span>
                  </div>
                  <div class="view">
                    <span class="who">{{ target }}看到</span>
                    <span class="say" v-html="paint(m.target, me, target, TGT)"></span>
                  </div>
                  <div class="view">
                    <span class="who">其他人看到</span>
                    <span class="say" v-html="paint(m.othersWithTarget, me, targetShow, null)"></span>
                  </div>
                </div>
                <div v-if="isSameName" class="note">
                  对象写成自己（{{ me }}）时，你自己和旁观者看到的对方名字会变成「自己」；而在对方（也就是你）自己的视角里，仍然显示为「你」。
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>

    <!-- ── 提示气泡 ───────────────────────────── -->
    <transition name="mv-fade">
      <div v-if="toast" class="toast">{{ toast }}</div>
    </transition>
  </div>
</template>

<script>
  import { postChatRequest, onResult } from '../../utils/api'

  const SRC = '#ff8800'
  const TGT = '#0088ff'

  /** 转义 HTML，避免玩家昵称里的尖括号破坏结构 */
  const esc = (s) =>
    String(s == null ? '' : s).replace(/[&<>"]/g, (c) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c])
    )

  /**
   * 把模板渲染成带高亮的 HTML。
   * 与后端 fanMovementMessageToCity 的替换口径保持一致：
   * {source} → 发起者昵称，{target} → 对象昵称，自己视角里的字面「你」单独着色。
   */
  const paint = (tpl, source, tgt, youColor) => {
    if (tpl == null || String(tpl).trim() === '') {
      return '<i class="missing">（该视角文案未配置，游戏内将使用兜底文案）</i>'
    }
    let t = esc(tpl)
    // 先用占位符保护，避免昵称里含有「你」字时被二次着色
    t = t.replace(/\{source\}/g, '\u0001').replace(/\{target\}/g, '\u0002')
    if (youColor) {
      t = t.replace(/你/g, '<b style="color:' + youColor + '">你</b>')
    }
    t = t.replace(/\u0001/g, '<b style="color:' + SRC + '">' + esc(source) + '</b>')
    t = t.replace(/\u0002/g, '<b style="color:' + TGT + '">' + esc(tgt) + '</b>')
    return t
  }

  const parseAlias = (a) => {
    const p = String(a == null ? '' : a).split('#')
    return {
      en: p[0] || '',
      cn: p[1] || '',
      py: p[2] || '',
      all: p.filter((x) => x && x.trim() !== '')
    }
  }

  /**
   * 聊天动作列表 —— 独立页（/chatMovement.html）与游戏内聊天框弹窗共用。
   * 预览用的两个名字由调用方通过 props 传入：页面在顶部说明卡里编辑，
   * 弹窗里则用玩家自己的昵称作默认值。
   */
  export default {
    name: 'ChatMovementList',
    props: {
      meName: { type: String, default: '星尘' },
      targetName: { type: String, default: 'wang' },
      /** 紧凑模式：体积更小、卡片去掉投影，用于内嵌弹窗 */
      compact: { type: Boolean, default: false }
    },
    data() {
      return {
        SRC,
        TGT,
        movements: [],
        loading: true,
        loadError: '',
        keyword: '',
        letter: '',
        openId: null,
        toast: '',
        toastTimer: null
      }
    },
    computed: {
      me() {
        return (this.meName || '').trim() || '某人'
      },
      target() {
        return (this.targetName || '').trim() || '对方'
      },
      isSameName() {
        return this.me === this.target
      },
      /** 与后端一致：对象就是自己时，{target} 会被替换成「自己」 */
      targetShow() {
        return this.isSameName ? '自己' : this.target
      },
      letters() {
        const set = new Set()
        this.movements.forEach((m) => {
          const en = parseAlias(m.alias).en
          if (en) set.add(en.toUpperCase().charAt(0))
        })
        return Array.from(set).sort()
      },
      filtered() {
        const q = this.keyword.trim().toLowerCase()
        return this.movements.filter((m) => {
          const a = parseAlias(m.alias)
          if (this.letter && a.en.toUpperCase().charAt(0) !== this.letter) return false
          if (!q) return true
          if (String(m.alias || '').toLowerCase().indexOf(q) > -1) return true
          return a.all.some((x) => x.toLowerCase().indexOf(q) > -1)
        })
      }
    },
    methods: {
      aliasOf(m) {
        return parseAlias(m.alias)
      },
      paint(tpl, source, tgt, youColor) {
        return paint(tpl, source, tgt, youColor)
      },
      load() {
        this.loading = true
        this.loadError = ''
        postChatRequest('/chatMovement/list', {})
          .then((resp) => {
            onResult(resp, {
              success: (data) => {
                this.movements = (data && data.data) || []
                this.loading = false
              },
              onError: (data) => {
                this.loadError = (data && data.message) || '动作列表载入失败'
                this.loading = false
              },
              onHttpError: () => {
                this.loadError = '动作列表载入失败，请稍后重试'
                this.loading = false
              }
            })
          })
          .catch(() => {
            this.loadError = '网络异常，请稍后重试'
            this.loading = false
          })
      },
      copy(txt) {
        const done = () => this.showToast('已复制「' + txt + '」，去聊天框粘贴试试')
        const fallback = () => {
          const ta = document.createElement('textarea')
          ta.value = txt
          ta.style.position = 'fixed'
          ta.style.opacity = '0'
          document.body.appendChild(ta)
          ta.select()
          try {
            document.execCommand('copy')
            done()
          } catch (e) {
            this.showToast(txt)
          }
          document.body.removeChild(ta)
        }
        if (navigator.clipboard && navigator.clipboard.writeText && window.isSecureContext) {
          navigator.clipboard.writeText(txt).then(done).catch(fallback)
        } else {
          fallback()
        }
      },
      showToast(msg) {
        this.toast = msg
        clearTimeout(this.toastTimer)
        this.toastTimer = setTimeout(() => {
          this.toast = ''
        }, 1800)
      }
    },
    mounted() {
      this.load()
    },
    beforeDestroy() {
      clearTimeout(this.toastTimer)
    }
  }
</script>

<style scoped>
.card {
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  margin-bottom: 12px;
}

/* ── 工具条 ── */
.tools-card {
  padding: 14px 24px;
}

.searchrow {
  display: flex;
  gap: 8px;
  align-items: center;
}

.search {
  flex: 1;
  min-width: 0;
  padding: 7px 12px;
  border: 1px solid #dcdfe6;
  border-radius: 8px;
  font-size: 13px;
  color: #303133;
  outline: none;
  background: #fff;
}

.search:focus {
  border-color: #409eff;
  box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.12);
}

.count {
  font-size: 12px;
  color: #909399;
  white-space: nowrap;
}

.letterbar {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 10px;
}

.letterbar button {
  min-width: 24px;
  height: 24px;
  padding: 0 6px;
  border: 1px solid transparent;
  background: #f5f7fa;
  border-radius: 5px;
  font-size: 12px;
  color: #606266;
  cursor: pointer;
  transition: 0.15s;
}

.letterbar button:hover {
  background: #ecf5ff;
  color: #409eff;
}

.letterbar button.on {
  background: #409eff;
  border-color: #409eff;
  color: #fff;
}

/* ── 列表 ── */
.list-card {
  padding: 14px 24px 22px;
}

.list {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
}

.item {
  border: 1px solid #ebeef5;
  border-radius: 9px;
  padding: 9px 11px;
  cursor: pointer;
  background: #fff;
  transition: 0.15s;
  overflow: hidden;
}

.item:hover {
  border-color: #c6e2ff;
  background: #fafcff;
}

.item.on {
  border-color: #409eff;
  background: #ecf5ff;
  grid-column: 1 / -1;
  cursor: default;
  padding: 0;
}

.item .row1 {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 6px;
}

.item .cn {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
}

.item .en {
  font-size: 11px;
  color: #c0c4cc;
  font-family: Consolas, Menlo, monospace;
}

.item .py {
  font-size: 11px;
  color: #909399;
  margin-top: 2px;
  font-family: Consolas, Menlo, monospace;
}

.detail {
  padding: 14px;
}

.detail .dtitle {
  display: flex;
  align-items: baseline;
  gap: 8px;
  padding-bottom: 10px;
  border-bottom: 1px solid #ebeef5;
}

.detail .dtitle .cn {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.detail .dtitle .en {
  font-size: 12px;
  color: #c0c4cc;
  font-family: Consolas, Menlo, monospace;
}

.detail .dtitle .close {
  margin-left: auto;
  font-size: 12px;
  color: #909399;
  background: none;
  border: 0;
  cursor: pointer;
}

.detail .dtitle .close:hover {
  color: #409eff;
}

.block {
  margin-top: 12px;
}

.block .bhead {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 7px;
}

.block .bhead .lab {
  font-size: 12px;
  color: #909399;
  min-width: 52px;
}

.chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 9px;
  border-radius: 999px;
  background: #fff;
  border: 1px solid #ffd8a8;
  color: #b5610a;
  font-family: Consolas, Menlo, monospace;
  font-size: 12px;
  cursor: pointer;
  user-select: none;
}

.chip:hover {
  background: #fff7e6;
}

.chip .cp {
  opacity: 0.45;
  font-size: 10px;
  font-family: inherit;
}

.chip:hover .cp {
  opacity: 0.9;
}

.views {
  background: #ffffee;
  border: 1px solid #f0e6c8;
  border-radius: 8px;
  padding: 9px 12px;
}

.view {
  display: flex;
  gap: 8px;
  font-size: 13px;
  line-height: 1.95;
  padding: 1px 0;
}

.view .who {
  flex: 0 0 auto;
  min-width: 88px;
  color: #606266;
  font-weight: 600;
  text-align: right;
  white-space: nowrap;
}

.view .say {
  color: #888888;
  word-break: break-word;
}

.view.self .who {
  color: #b5610a;
}

.note {
  margin-top: 8px;
  padding: 6px 10px;
  background: #fdf6ec;
  border-left: 3px solid #e6a23c;
  border-radius: 4px;
  font-size: 11.5px;
  color: #b88230;
  line-height: 1.7;
}

.empty {
  padding: 40px 0;
  text-align: center;
  color: #c0c4cc;
  font-size: 13px;
}

.retry {
  margin-top: 10px;
  padding: 5px 16px;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  background: #fff;
  color: #409eff;
  font-size: 12px;
  cursor: pointer;
}

.retry:hover {
  background: #ecf5ff;
}

/* ── 提示气泡 ── */
/* z-index 要高过 el-dialog（Element 从 2000 起递增） */
.toast {
  position: fixed;
  left: 50%;
  bottom: 38px;
  transform: translateX(-50%);
  background: rgba(48, 49, 51, 0.92);
  color: #fff;
  font-size: 12px;
  padding: 7px 16px;
  border-radius: 6px;
  z-index: 3000;
  max-width: 86vw;
  text-align: center;
}

.mv-fade-enter-active,
.mv-fade-leave-active {
  transition: opacity 0.2s;
}

.mv-fade-enter,
.mv-fade-leave-to {
  opacity: 0;
}

/* ── 紧凑模式（内嵌弹窗用）──────────────────
   去掉卡片投影与外边距：弹窗本身已是「一张卡」，
   内层再套一层阴影会显得很脏。 */
.cm-list.compact .card {
  border-radius: 8px;
  box-shadow: none;
  border: 1px solid #ebeef5;
  margin-bottom: 8px;
}

.cm-list.compact .tools-card {
  padding: 10px 12px;
}

.cm-list.compact .list-card {
  padding: 12px 12px 14px;
}

.cm-list.compact .item {
  padding: 7px 9px;
}

.cm-list.compact .item .cn {
  font-size: 13px;
}

.cm-list.compact .detail {
  padding: 12px;
}

.cm-list.compact .empty {
  padding: 26px 0;
}

/* ── 响应式 ── */
@media (max-width: 600px) {
  .list {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .tools-card {
    padding: 12px 16px;
  }

  .list-card {
    padding: 12px 16px 18px;
  }

  .list {
    gap: 7px;
  }

  .item {
    padding: 8px 9px;
  }

  .item .cn {
    font-size: 13px;
  }

  .detail {
    padding: 12px;
  }

  .views {
    padding: 8px 10px;
  }

  .view {
    font-size: 12.5px;
    gap: 6px;
  }

  .view .who {
    min-width: 72px;
    font-size: 12px;
  }

  .cm-list.compact .tools-card,
  .cm-list.compact .list-card {
    padding: 10px;
  }
}
</style>

<style>
/* v-html 生成的内容不受 scoped 约束，文案缺失提示写在这里 */
.cm-list .say .missing {
  color: #c0c4cc;
  font-style: normal;
  font-size: 12px;
}
</style>
